import { onActivated, onBeforeUnmount, onDeactivated, onMounted } from "vue";

type UsePeriodicRefreshOptions = {
  intervalMs?: number;
  enabled?: () => boolean;
};

export const usePeriodicRefresh = (
  callback: () => void | Promise<void>,
  options: UsePeriodicRefreshOptions = {},
) => {
  const intervalMs = options.intervalMs ?? 30_000;
  const isEnabled = options.enabled ?? (() => true);

  let timer: ReturnType<typeof window.setInterval> | null = null;
  let isRunning = false;

  const run = async () => {
    if (
      typeof window === "undefined" ||
      typeof document === "undefined" ||
      document.visibilityState !== "visible" ||
      !isEnabled() ||
      isRunning
    ) {
      return;
    }

    isRunning = true;

    try {
      await callback();
    } finally {
      isRunning = false;
    }
  };

  const start = () => {
    if (typeof window === "undefined" || timer !== null) {
      return;
    }

    timer = window.setInterval(() => {
      void run();
    }, intervalMs);
  };

  const stop = () => {
    if (typeof window === "undefined" || timer === null) {
      return;
    }

    window.clearInterval(timer);
    timer = null;
  };

  const handleVisibilityChange = () => {
    if (
      typeof document === "undefined" ||
      document.visibilityState !== "visible"
    ) {
      return;
    }

    void run();
  };

  onMounted(() => {
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", handleVisibilityChange);
    }

    start();
  });

  onActivated(() => {
    start();
  });

  onDeactivated(() => {
    stop();
  });

  onBeforeUnmount(() => {
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    }

    stop();
  });
};
